<template>
<div>
  <span class="tit">이벤트 선택</span>
  <el-popover :width="800" placement="bottom" trigger="click" v-model="showEventPopover">
    <el-table-wrapper v-loading="loading" :max-height="400" :columns="columns" :data="tableData" row-class-name="c-pointer" :pagination="false" @cell-click="selectEventHist">
      <template slot-scope="scope" slot="eventlevel-slot">
        <w-eventlevel-cell :eventlevel-id="scope.row.eventlevelId"/>
      </template>
    </el-table-wrapper>
    <el-input :class="$style.pointer" style="width:calc(100% - 90px);" slot="reference" v-model="selectedEventHistLabel" placeholder="이벤트를 선택해주십시오." readonly />
  </el-popover>
</div>
</template>

<script>
import isEmpty from 'lodash/isEmpty';
import isNil from 'lodash/isNil';
import { EMPTY_TEXT, getValueAfterCheckEmpty } from '@/utils/string';
import { getDateToString } from '@/utils/date';
import msgUtil from '@/utils/message';
import SmartAnalysisApi from '@/api/common/smart-analysis';
import WEventlevelCell from '@/components/table/EventlevelCell';

/**
 * 이벤트 이력 선택 컴포넌트
 * 1) Events
 * select: 이벤트 이력 선택 이벤트
 * - paramter: selectedEventHistData (Object / 선택한 이벤트 이력 데이터)
 * reset: 컴포넌트 초기화 이벤트
 */
export default {
  name: 'WEventHistSelectComponent',
  components: {
    WEventlevelCell,
  },
  props: {
    /**
     * 이벤트 이력 seqNumber 배열 (사전 예측 이벤트 이력 seqNumber 배열)
     * @type {Object}
     */
    eventHistSeqNumbers: {
      type: Array,
    },
    /**
     * 사전 예측 이벤트 기준으로 제공 여부.
     * 사전 예측 이벤트 seqNumber를 전달할 경우 해당 값을 true로 설정해야한다.
     * @type {Object}
     */
    isPredictEvent: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      loading: false,
      showEventPopover: false,
      eventHistList: [],
      tableData: [],
      selectedEventHistData: {},
      selectedEventHistLabel: null,
      columns: [
        {
          prop: 'eventlevelName',
          label: '등급',
          width: 76,
          scopedSlot: 'eventlevel-slot',
          headerAlign: 'center',
          columnKey: 'eventlevelId',
          searchable: true,
          searchType: 'eventlevel',
        },
        {
          prop: 'objName',
          label: '장비명',
          width: 150,
          sortable: true,
        },
        // {
        //   prop: 'managetypeName',
        //   label: '관리구분',
        //   sortable: true,
        // },
        {
          prop: 'rsctypeName',
          label: '자원구분',
          width: 100,
          sortable: true,
        },
        // {
        //   prop: 'rscName',
        //   label: '자원',
        //   sortable: true,
        // },
        // {
        //   prop: 'rscKeyName',
        //   label: '자원키',
        //   sortable: true,
        // },
        {
          prop: 'eventMessage',
          label: '장애 메시지',
          sortable: true,
        },
        {
          prop: 'checkDatetime',
          label: '발생 시각',
          width: 130,
          sortable: true,
          formatter: this.getDateFormatter,
        },
      ],
    };
  },
  watch: {
    eventHistSeqNumbers: {
      immediate: true,
      handler(value) {
        if (isEmpty(value)) {
          this.eventHistList = [];
        } else if (this.isPredictEvent) {
          this.loadPredictEventHist();
        } else {
          this.loadEventHist();
        }
      },
    },
    eventHistList: {
      handler(value) {
        if (isEmpty(value)) {
          this.reset();
        } else {
          this.tableData = this.eventHistList;
          if (!isEmpty(this.tableData)) {
            this.selectEventHist(this.tableData[0]);
          }
        }
      },
    },
  },
  methods: {
    isEmpty,
    /**
     * 컴포넌트를 초기화한다.
     * @author Ju
     */
    reset() {
      this.tableData = [];
      this.selectedEventHistData = {};
      this.selectedEventHistLabel = null;
      this.$emit('reset');
    },
    /**
     * 이벤트 이력 데이터를 로드한다.
     * @author Ju
     */
    loadEventHist() {
      this.loading = true;
      SmartAnalysisApi.getEventHistBySeqNumbers(this.eventHistSeqNumbers)
        .then((response) => {
          if (response.data.success) {
            this.eventHistList = response.data.data;
          } else {
            this.$message(msgUtil.getResultMsgData(msgUtil.action.VIEW, '이벤트 이력 정보', false));
          }
        })
        .catch((e) => {
          this.$message(msgUtil.getResultMsgData(msgUtil.action.VIEW, '이벤트 이력 정보', false));
          throw new Error(e);
        })
        .finally(() => {
          this.loading = false;
        });
    },
    /**
     * 사전 예측 이벤트 이력 데이터를 로드한다.
     * @author Ju
     */
    loadPredictEventHist() {
      this.loading = true;
      SmartAnalysisApi.getPredictEventHistBySeqNumbers(this.eventHistSeqNumbers)
        .then((response) => {
          if (response.data.success) {
            this.eventHistList = response.data.data;
          } else {
            this.$message(msgUtil.getResultMsgData(msgUtil.action.VIEW, '이벤트 이력 정보', false));
          }
        })
        .catch((e) => {
          this.$message(msgUtil.getResultMsgData(msgUtil.action.VIEW, '이벤트 이력 정보', false));
          throw new Error(e);
        })
        .finally(() => {
          this.loading = false;
        });
    },
    /**
     * 이벤트 이력을 선택한다.
     * @author Ju
     * @param  {[type]} row [description]
     */
    selectEventHist(row) {
      this.selectedEventHistData = row;
      this.selectedEventHistLabel = `[${getValueAfterCheckEmpty(row.objName)}] ${getValueAfterCheckEmpty(row.eventMessage)}`;
      this.showEventPopover = false;
      this.$emit('select', this.selectedEventHistData);
    },
    /**
     * 날짜를 지정한 포맷으로 변환한다.
     * @param  {[type]} row    [description]
     * @param  {[type]} column [description]
     * @param  {[type]} value  [description]
     * @return {[type]}        [description]
     */
    getDateFormatter(row, column, value) {
      if (!isNil(value)) {
        return getDateToString(value);
      }
      return EMPTY_TEXT;
    },
  },
};
</script>

<style module>
.pointer input {
  cursor: pointer;
}
</style>



// WEBPACK FOOTER //
// src/components/popup/EventSmartAnalyzerPopup/components/EventHistSelectComponent.vue